/**
 * AffiliateProvidersCard — Admin editor for affiliate tags per marketplace.
 *
 * Each marketplace has its own click-tracking parameter (Amazon=`tag`,
 * Flipkart=`affid`, everyone else defaults to `utm_source`). Empty string
 * clears the tag. Updates take effect on the very next AI Cart click.
 */
import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Save, Link2, TrendingUp, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";

interface Marketplace {
  key: string;
  name: string;
  domain: string;
  aff_param: string;
  aff_default: string | null;
  positioning: string;
  tag: string;
}

interface ConfigPayload {
  marketplaces: Marketplace[];
  configured: number;
  total: number;
}

interface Analytics {
  totals: { all_time: number; last_hour: number; last_24h: number; last_7d: number };
  top_marketplaces: { marketplace: string; marketplace_name: string; clicks: number }[];
  recent_clicks: { marketplace: string; target_url: string; clicked_at: string }[];
}

export const AffiliateProvidersCard: React.FC = () => {
  const qc = useQueryClient();
  const cfg = useQuery<ConfigPayload>({
    queryKey: ["aff-config"],
    queryFn: async () => (await api.get("/aff/config")).data,
  });
  const stats = useQuery<Analytics>({
    queryKey: ["aff-analytics"],
    queryFn: async () => (await api.get("/aff/analytics/summary")).data,
    refetchInterval: 30_000,
  });

  const [draft, setDraft] = useState<Record<string, string>>({});
  useEffect(() => {
    if (cfg.data) {
      const seed: Record<string, string> = {};
      for (const m of cfg.data.marketplaces) seed[m.key] = m.tag || "";
      setDraft(seed);
    }
  }, [cfg.data]);

  const save = useMutation({
    mutationFn: async () => (await api.put("/aff/config", { tags: draft })).data,
    onSuccess: (data: ConfigPayload) => {
      toast.success(`Saved — ${data.configured}/${data.total} marketplaces configured`);
      qc.setQueryData(["aff-config"], data);
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Save failed"),
  });

  if (cfg.isLoading) {
    return (
      <div className="sg-card p-6 mt-6" data-testid="affiliate-providers-card">
        <Loader2 className="h-5 w-5 animate-spin text-[color:var(--sg-muted)]" />
      </div>
    );
  }

  const rows = cfg.data?.marketplaces || [];
  const dirty = Object.entries(draft).some(([k, v]) =>
    v !== (rows.find(r => r.key === k)?.tag || ""),
  );

  return (
    <div className="sg-card p-6 mt-6" data-testid="affiliate-providers-card">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-1">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 grid place-items-center text-white shrink-0">
            <Link2 className="h-4 w-4" />
          </div>
          <div>
            <div className="sg-label">Affiliate outbound · AI Cart™</div>
            <p className="text-sm text-[color:var(--sg-muted)] max-w-2xl mt-0.5">
              Every AI Cart™ click is tracked and 302-redirected. Paste your
              affiliate / partner ID per marketplace to unlock revenue on
              outbound. Empty tags still track clicks and add UTM tags.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {stats.data && (
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-emerald-600" data-testid="aff-total-clicks">
              <TrendingUp className="h-3 w-3" />
              {stats.data.totals.all_time} clicks · {stats.data.totals.last_24h} today
            </span>
          )}
          <span className="text-[10px] uppercase tracking-widest text-[color:var(--sg-muted)]" data-testid="aff-configured-count">
            {cfg.data?.configured || 0}/{cfg.data?.total || 0} configured
          </span>
          <Button
            data-testid="aff-save-btn"
            size="sm"
            disabled={!dirty || save.isPending}
            onClick={() => save.mutate()}
            className="bg-[color:var(--sg-fg)] text-white hover:bg-black"
          >
            {save.isPending ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1" />}
            Save
          </Button>
        </div>
      </div>

      <div className="mt-5 grid sm:grid-cols-2 gap-2">
        {rows.map((m) => {
          const clicks = stats.data?.top_marketplaces.find(t => t.marketplace === m.key)?.clicks || 0;
          const isDirty = draft[m.key] !== (m.tag || "");
          return (
            <div
              key={m.key}
              data-testid={`aff-row-${m.key}`}
              className={`rounded-xl border px-3 py-2.5 bg-white flex items-center gap-3 ${
                isDirty ? "border-orange-400 bg-orange-50/60" : "border-[color:var(--sg-border)]"
              }`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <div className="text-sm font-semibold text-slate-800 truncate">{m.name}</div>
                  <span className="text-[9px] font-mono text-slate-400">{m.domain}</span>
                  {clicks > 0 && (
                    <span className="text-[9px] uppercase tracking-widest text-emerald-600 bg-emerald-50 border border-emerald-200 rounded-full px-1.5">
                      {clicks} clicks / 7d
                    </span>
                  )}
                </div>
                <div className="text-[10px] text-[color:var(--sg-muted)] leading-snug truncate">
                  Query param: <code className="font-mono">{m.aff_param}</code> · {m.positioning}
                </div>
              </div>
              <input
                type="text"
                data-testid={`aff-input-${m.key}`}
                value={draft[m.key] || ""}
                onChange={(e) => setDraft(d => ({ ...d, [m.key]: e.target.value }))}
                placeholder={m.aff_default || `your-${m.key}-tag`}
                className="w-40 shrink-0 text-sm px-2 py-1 rounded-md bg-white border border-slate-300 font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
              />
            </div>
          );
        })}
      </div>

      {stats.data && stats.data.recent_clicks.length > 0 && (
        <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-3" data-testid="aff-recent-clicks">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Recent outbound clicks</div>
          <div className="space-y-1">
            {stats.data.recent_clicks.slice(0, 5).map((c, i) => (
              <div key={i} className="flex items-center justify-between text-[11px] gap-2">
                <div className="flex items-center gap-1.5 min-w-0">
                  <ExternalLink className="h-3 w-3 text-slate-400 shrink-0" />
                  <span className="font-semibold text-slate-700">{c.marketplace}</span>
                  <span className="text-slate-400 truncate max-w-md">{c.target_url}</span>
                </div>
                <span className="text-slate-400 font-mono shrink-0">
                  {new Date(c.clicked_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AffiliateProvidersCard;
